import {
  drybulkCargoOperationsService,
  type CargoOperationDetail,
  type CreateCargoOperationRequest,
} from "@services/drybulkCargoOperationsApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { DRYBULK_CARGO_OPS_QUERY_KEYS } from "../constants";

/** DBCO-0028 - create once Operation Type and Total Quantity are both set (steering, AC6). */
export const useCreateCargoOperation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateCargoOperationRequest) =>
      runTransaction(
        {
          module: "drybulk_cargo_operations",
          screen: "AddCargoOperation",
          action: "create_cargo_operation",
          entityType: "cargo_operation",
          entityId: request.id,
        },
        (transactionId) => drybulkCargoOperationsService.create(request, { transactionId }),
      ),
    // DBCO-0031: a new operation is a new grid row.
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });
    },
  });
};

/** DBCO-0028 - revise Total Quantity on the draft (R-03). Operation Type has no equivalent: immutable. */
export const useUpdateTotalQuantity = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, totalQuantity }: { id: string; totalQuantity: number }) =>
      runTransaction(
        {
          module: "drybulk_cargo_operations",
          screen: "AddCargoOperation",
          action: "update_cargo_operation_total_quantity",
          entityType: "cargo_operation",
          entityId: id,
        },
        (transactionId) =>
          drybulkCargoOperationsService.updateTotalQuantity(id, totalQuantity, { transactionId }),
      ),
    // DBCO-0029: the cached detail (if the sequence section loaded it) carries the server's new figures.
    onSuccess: (updated, { id }) => {
      queryClient.setQueryData<CargoOperationDetail>(
        DRYBULK_CARGO_OPS_QUERY_KEYS.detail(id),
        (detail) =>
          detail
            ? {
                ...detail,
                totalQuantity: updated.totalQuantity,
                totalQuantityRemained: updated.totalQuantityRemained,
              }
            : detail,
      );
      // DBCO-0031: the grid row shows the total.
      void queryClient.invalidateQueries({ queryKey: DRYBULK_CARGO_OPS_QUERY_KEYS.searchAll() });
    },
  });
};
